<template>
  <div class="partnership-information-container">
    <div class="partnership-settings-container">
      <div class="partnership-settings">
        <div class="sharing-partner-data">
          <partnership-view-data
            :label="$t('common.invite')"
            :description="inviteLink"
            @click="onCopy"
          />
          <div class="repost-social-button" @click="onShownSharingDialog"></div>
        </div>
        <div
          v-if="!isMobile"
          class="report-button"
          :class="{ disabled: isEmptyParnterList }"
          @click="handleClickToReport"
        >
          <span class="report-download-icon"></span>
          {{ $t('common.report') }}
        </div>
      </div>
      <div class="partnership-settings">
        <div class="partnership-deductions">
          <partnership-view-data
            :label="$t('contributions.to')"
            icon="settings"
            :description=" partner && partner.parentProjectName ? partner.parentProjectName :''"
            @click="onShownChangeProject"
          />
        </div>
        <div
          v-if="isShownRateButtonByDesktop"
          class="rate-control-button"
          :class="{ active: partner.rateChangePermission }"
          @click="onShownChangePartnerRate"
        >
          {{ displayPartnerRate }}
        </div>
      </div>
      <div v-if="isShownRateButtonByMobile" class="partnership-settings">
        <div
          class="rate-control-button"
          :class="{ active: partner.rateChangePermission }"
          @click="onShownChangePartnerRate"
        >
          {{ displayPartnerRate }}
        </div>
      </div>
      <div v-if="isMobile" class="partnership-settings">
        <div
          class="report-button"
          :class="{ disabled: isEmptyParnterList }"
          @click="handleClickToReport"
        >
          <span class="report-download-icon"></span>
          {{ $t('common.report') }}
        </div>
      </div>
    </div>

    <cs-dialog v-model="isShownSocialSharing">
      <dialog-default-layout>
        <h2 slot="title">{{ $t('share.on.social.media') }}</h2>
        <div slot="content">
          <div class="social-share-conatiner">
            <socials-share
              :url="partner.link"
              :title="`${$t('referral.link')} | capusta.space`"
              :description="partner.parentProjectName"
            />
          </div>
        </div>
      </dialog-default-layout>
    </cs-dialog>

    <div ref="mainContainer">
      <cs-dialog v-model="isShownChangeProject">
        <dialog-list-layout @scroll-end="$emit('load-next')">
          <capusta-option-panel
            v-for="project in preparedProjectList"
            :key="project.id"
            :option="project"
            @click="onShownConfirmChangeProject"
          >
            <div slot-scope="props" class="option-container">
              <project-logo :project="props.option" />
              <section>
                <p>{{ props.option.description }}</p>
                <p><small>{{ $t(props.option.label) }}</small></p>
              </section>
            </div>
          </capusta-option-panel>
        </dialog-list-layout>
      </cs-dialog>
    </div>
    <cs-dialog v-model="isShownConfirmChangeProject">
      <dialog-default-layout>
        <div slot="title" class="dialog-layout-confirmation">
          <h2>{{
            $t('you.want.change.project.for.deductions.on', { projectName: selectedProjectName })
          }}</h2>
        </div>
        <div slot="footer" class="confirmation-control-buttons">
          <cs-button :width="190" @click="onChangeProject">
            {{ $t('common.yes') }}
          </cs-button>
          <cs-button type="transparent" @click="onCloseConfirmChangeProject">
            {{ $t('common.cancel') }}
          </cs-button>
        </div>
      </dialog-default-layout>
    </cs-dialog>

    <cs-dialog v-model="isShownChangePartnerRate">
      <dialog-default-layout>
        <h2 slot="title" class="dialog-layout-title">
          {{ $t('enter.rate') }}
          <a href="https://help.capusta.space/partner/rate" target="_blank"></a>
        </h2>
        <template slot="content">
          <div class="dialog-layout-text">{{ displayMaxAvailableRate }}</div>
          <div class="dialog-layout-input">
            <capusta-input
              v-model="partnerRate"
              v-mask="percentTokens"
              :error="!isValidPartnerRate"
              :validation="validatePartnerRate"
              :label="`${$t('common.percent')}, %`"
              placeholder="0.5"
            />
          </div>
        </template>
        <div slot="footer" class="confirmation-control-buttons">
          <cs-button
            :width="200"
            :disabled="!isValidPartnerRate"
            @click="onShownConfirmPartnerRate"
          >
            {{ $t('common.apply') }}
          </cs-button>
          <cs-button type="transparent" @click="onCloseChangePartnerRate">
            {{ $t('common.cancel') }}
          </cs-button>
        </div>
      </dialog-default-layout>
    </cs-dialog>

    <cs-dialog v-model="isShownConfirmPartnerRate">
      <dialog-default-layout>
        <div slot="title" class="dialog-layout-confirmation">
          <h2>{{ $t('you.want.change.rate.for.all.partners') }}</h2>
        </div>
        <div slot="footer" class="confirmation-control-buttons">
          <cs-button :width="190" @click="onChangePartnerRate">
            {{ $t('common.yes') }}
          </cs-button>
          <cs-button type="transparent" @click="onCloseChangePartnerRate">
            {{ $t('common.cancel') }}
          </cs-button>
        </div>
      </dialog-default-layout>
    </cs-dialog>
  </div>
</template>

<script>
import { mask } from 'vue-the-mask';
import { isMobile, isTablet } from 'mobile-device-detect';

import { isBetaTest } from 'lib/system/env';

import CapustaOptionPanel from 'modules/CapustaLayout/CapustaOptionPanel';
import DialogListLayout from 'modules/Dialogs/ListLayout';
import DialogDefaultLayout from 'modules/Dialogs/DefaultLayout';

import CapustaInput from 'molecules/capusta-input/CapustaInput';
import SocialsShare from '@/components/otherElements/SocialsShare';

import PartnershipViewData from './PartnershipViewData';
import ProjectLogo from 'molecules/project/ProjectLogo';

import { EVENT_BUS } from '@/eventBus';

import { formattedProjectForDeductions } from '../helpers';

export default {
  name: 'PartnershipSettings',
  directives: {
    mask,
  },
  components: {
    CapustaInput,
    CapustaOptionPanel,
    DialogListLayout,
    DialogDefaultLayout,
    ProjectLogo,
    SocialsShare,
    PartnershipViewData,
  },
  props: {
    partner: {
      type: Object,
      required: true,
    },
    projects: {
      type: Array,
      default: () => [],
    },
  },
  mounted() {
    this.onChangeOrientation();

    window.addEventListener('resize', this.onChangeOrientation, false);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onChangeOrientation, false);
  },
  data() {
    return {
      isMobile,
      isTablet,
      partnerRate: '',
      isShownSocialSharing: false,
      isShownChangeProject: false,
      isShownConfirmChangeProject: false,
      isShownChangePartnerRate: false,
      isShownConfirmPartnerRate: false,
      selectedProject: null,
      orientation: null,
      percentTokens: {
        mask: 'CCCC',
        tokens: {
          'C': {
            pattern: /[0-9/\\.]/,
          },
        },
      },
      isBetaTest: isBetaTest(),
    };
  },
  computed: {
    inviteLink() {
      return (this.partner && this.partner.link) || '';
    },
    parentProjectId() {
      return this.partner.parentProjectId;
    },
    preparedProjectList() {
      return formattedProjectForDeductions(this.projects, this.parentProjectId);
    },
    maxAvailableRate() {
      return this.partner.maxAvailableRate;
    },
    displayMaxAvailableRate() {
      return `${this.$t('no.more')} ${this.maxAvailableRate}%`;
    },
    displayPartnerRate() {
      return `${this.$t('common.rate')} ${this.partner.rate}%`;
    },
    isEmptyParnterList() {
      return this.$validate.isEmptyArray(this.partner.childProjects);
    },
    isValidPartnerRate() {
      if (this.$validate.isEmptyString(this.partnerRate)) {
        return false;
      }

      return this.validatePartnerRate();
    },
    selectedProjectName() {
      if (this.$validate.isNull(this.selectedProject)) {
        return '';
      }

      return this.selectedProject.name;
    },
    formattedRate() {
      const RATE_REGEXP = /^\d+[.]{0,1}\d{0,1}/;
      const formattedValue = String(this.partnerRate).match(RATE_REGEXP)[0];

      return formattedValue;
    },
    isShownRateButtonByMobile() {
      if (this.isTablet) {
        return false;
      }

      return this.orientation === 'portrait-primary';
    },
    isShownRateButtonByDesktop() {
      if (this.isTablet) {
        return true;
      }

      return this.orientation === 'landscape-primary';
    },
  },
  watch: {
    partner: {
      deep: true,
      immediate: true,
      handler(val) {
        this.partnerRate = val.rate;
      },
    },
  },
  methods: {
    handleClickToReport() {
      if (this.isEmptyParnterList) {
        return;
      }

      EVENT_BUS.$emit('EVENT_GET_REPORT_LIST', 'PARTNER');
    },
    validatePartnerRate() {
      if (Number(this.partnerRate) < 0) {
        return false;
      }

      return Number(this.partnerRate) <= Number(this.maxAvailableRate);
    },
    onChangeOrientation() {
      if (window.screen.orientation) {
        this.orientation = window.screen.orientation.type;
        return;
      }

      this.orientation = document.documentElement.clientHeight > document.documentElement.clientWidth
        ? 'portrait-primary'
        : 'landscape-primary';
    },
    onCopy() {
      this.$copyText(this.partner.link);

      this.$alert({
        type: 'ok',
        text: this.$t('link.copied'),
      });
    },
    onShownSharingDialog() {
      this.isShownSocialSharing = true;
    },
    onShownChangeProject() {
      this.isShownChangeProject = true;
    },
    onShownConfirmChangeProject(data) {
      this.selectedProject = data;
      this.isShownChangeProject = false;
      this.isShownConfirmChangeProject = true;
    },
    onCloseConfirmChangeProject() {
      this.selectedProject = null;
      this.isShownConfirmChangeProject = false;
    },
    onChangeProject() {
      this.$emit('update-partner', this.selectedProject);
      this.onCloseConfirmChangeProject();
    },
    onShownChangePartnerRate() {
      if (!this.partner.rateChangePermission) {
        return;
      }

      this.isShownChangePartnerRate = true;
    },
    onShownConfirmPartnerRate() {
      this.isShownChangePartnerRate = false;
      this.isShownConfirmPartnerRate = true;
    },
    onCloseChangePartnerRate() {
      this.isShownChangePartnerRate = false;
      this.isShownConfirmPartnerRate = false;
    },
    onChangePartnerRate() {
      this.$emit('change-rate', this.formattedRate);
      this.onCloseChangePartnerRate();
    },
  },
};
</script>

<style lang="sass" scoped>
  .partnership-information-container
    width: 100%
    max-width: 620px
    position: relative
    margin: 0 auto
    z-index: 1000

  .partnership-settings-container
    padding: 0 20px

    +respond-to('small')
      padding: 0

  .partnership-settings
    &:not(:last-child)
      margin-bottom: 20px

  .partnership-settings
    display: flex

    +respond-to('small')
      > *
        &:not(:last-child)
          margin-right: 20px

  .sharing-partner-data
    width: 100%
    display: flex
    justify-content: space-between
    background: transparent
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.15)

    & .data-view-container
      width: calc(100% - 40px)

    +respond-to('small')
      background: #27283C
      border-radius: 50px

    +respond-to('medium')
      max-width: 500px

      & .data-view-container
        width: calc(100% - 80px)

  .repost-social-button
    width: 40px
    height: 40px
    flex: none
    background-color: #27283C
    background-repeat: no-repeat
    background-position: center
    background-size: 18px
    border-radius: 50px
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.15)
    cursor: pointer

    +respond-to('small')
      width: 80px
      height: auto
      border-radius: 0
      background-color: transparent
      box-shadow: none

    #{theme('eclipse')} &
      background-image: get-icon('share', #9B51E0)

    #{theme('obsidian')} &
      background-image: get-icon('share', $pure-white)

    &:hover
      #{theme('eclipse')} &
        background-image: get-icon('share', #212121)

      #{theme('obsidian')} &
        background-image: get-icon('share', #9B51E0)

  .report-button
    width: 100%
    height: 40px
    display: flex
    align-items: center
    justify-content: center
    font-weight: 500
    font-size: 12px
    line-height: 15px
    background-color: rgba(155, 81, 224, 1)
    border-radius: 50px
    transition: opacity linear .3s
    cursor: pointer

    &:hover
      opacity: .8

    &.disabled
      background: #3A3949
      box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.15)
      color: rgba(218, 218, 218, .5)
      cursor: default

      &:hover
        opacity: 1

      & .report-download-icon
          opacity: .3

  .report-download-icon
    width: 14px
    height: 17px
    margin-right: 7px
    background-image: url('~@/assets/svg/common/download-report.svg')
    background-repeat: no-repeat
    background-position: center

  .partnership-deductions
    width: 100%

    +respond-to('small')
      max-width: 460px

  .rate-control-button
    width: 100%
    height: 40px
    display: flex
    justify-content: center
    align-items: center
    font-weight: 500
    font-size: 14px
    line-height: 18px
    color: rgba(255, 255, 255, .5)
    background: #181823
    border-radius: 50px
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.15)
    transition: all .2s ease

    +respond-to('small')
      width: 140px
      flex: none

    &.active
      color: #7CB342
      background: #27283C
      cursor: pointer

      &:hover
        color: #9B51E0

  .option-container
    flex-wrap: nowrap
    align-items: center
    padding: 20px

    section
      width: 100%
      padding-left: 20px
      overflow: hidden

    p
      font-weight: 500
      font-size: 14px
      line-height: 20px
      white-space: nowrap
      text-overflow: ellipsis
      overflow: hidden
      color: #fff

      &:first-child
        display: -webkit-box
        -webkit-line-clamp: 1
        -webkit-box-orient: vertical

    small
      font-weight: 700
      font-size: 12px
      line-height: 18px
      color: #fff
      opacity: .5

  .dialog-layout-title
    display: flex
    align-items: center
    justify-content: space-between

    a
      width: 24px
      height: 24px
      background-image: url('~@/assets/svg/common/help.svg')
      background-repeat: no-repeat
      background-position: center
      background-size: cover

  .dialog-layout-confirmation
    margin-bottom: 50px

  .dialog-layout-text
    font-weight: 500
    font-size: 14px
    line-height: 20px
    color: #fff
    opacity: .75

  .dialog-layout-input
    position: relative
    margin: 20px 0 40px

    &:after
      content: ''
      width: 58px
      height: 60px
      position: absolute
      right: -50px
      top: 50%
      background-image: url('~@/assets/svg/common/input-decoration.svg')
      background-repeat: no-repeat
      background-position: center
      transform: translateY(-50%)
      z-index: 1000

  .confirmation-control-buttons
    display: flex
    flex-direction: column
    align-items: center
    justify-content: space-between

    *
      &:not(:last-child)
        margin-bottom: 20px

    +respond-to('small')
      flex-direction: row

      *
        &:not(:last-child)
          margin-bottom: 0

  .change-rate-button
    font-size: 1.12rem
    font-weight: 600
    width: 100%
    display: block
    padding: 0.8rem
    color: $soft-white
    border-radius: 2.48rem
    background: transparent
</style>
